// Shared types, section config and date helpers for the read-only register.

export type Rec = Record<string, string | number | null>

export type SectionKey = 'domains' | 'hosting' | 'accounts' | 'subscriptions' | 'phone_numbers'

export interface RegisterData {
  domains: Rec[]
  hosting: Rec[]
  accounts: Rec[]
  subscriptions: Rec[]
  phone_numbers: Rec[]
  meta: { generatedAt: string }
}

export interface Field {
  key: string
  label: string
  kind?: 'date' | 'bool'
  /** how the edit modal renders this field; omit to skip it in the form */
  input?: 'text' | 'textarea' | 'date' | 'number' | 'select' | 'bool'
  options?: string[]
  required?: boolean
}

export const ENTITY_NAMES = [
  'Analytix', 'Analytix Arabia', 'Analytix India', 'Analytix Technology',
  'Analytix Bizcare', 'Analytix China', 'Setupz', 'CCC',
]

export interface Section {
  key: SectionKey
  label: string
  lede: string
  /** date column that drives expiry grouping / the calendar, if any */
  dateField?: string
  titleField: string
  meta: (r: Rec) => string
  fields: Field[]
  link?: (r: Rec) => string
  /** a business-continuity warning to surface on the row, or '' */
  flag?: (r: Rec) => string
  /** carries an encrypted password (brief §5) */
  secret?: boolean
}

const s = (v: unknown) => (v == null ? '' : String(v))
const join = (...xs: string[]) => xs.filter(Boolean).join('  ·  ')
const PERSONAL_EMAIL = /@(gmail|googlemail|yahoo|ymail|hotmail|outlook|live|icloud|qq|163)\./i

export const SECTIONS: Record<SectionKey, Section> = {
  domains: {
    key: 'domains',
    label: 'Domains',
    lede: 'Every domain we own, and when it runs out.',
    dateField: 'expiry_date',
    titleField: 'name',
    meta: (r) => join(s(r.registrar), s(r.server), s(r.purpose)),
    fields: [
      { key: 'name', label: 'Domain', input: 'text', required: true },
      { key: 'registrar', label: 'Registrar', input: 'text' },
      { key: 'server', label: 'Server / host', input: 'text' },
      { key: 'purpose', label: 'What it is for', input: 'text' },
      { key: 'entity', label: 'Belongs to', input: 'select', options: ENTITY_NAMES },
      { key: 'expiry_date', label: 'Expires', kind: 'date', input: 'date' },
      { key: 'auto_renew', label: 'Auto-renew', kind: 'bool', input: 'bool' },
      { key: 'last_verified_at', label: 'Last verified', kind: 'date' },
      {
        key: 'criticality',
        label: 'Criticality',
        input: 'select',
        options: ['high', 'medium', 'low'],
      },
      { key: 'notes', label: 'Notes', input: 'textarea' },
    ],
    link: (r) => (r.name ? `https://${r.name}` : ''),
  },
  hosting: {
    key: 'hosting',
    secret: true,
    label: 'Hosting & servers',
    lede: 'Registrar, hosting and server logins.',
    dateField: 'expiry_date',
    titleField: 'name',
    meta: (r) => join(s(r.kind), s(r.username), s(r.owner)),
    fields: [
      { key: 'name', label: 'Account', input: 'text', required: true },
      {
        key: 'kind',
        label: 'Type',
        input: 'select',
        options: ['registrar', 'hosting', 'control panel', 'server', 'cloud', 'reseller'],
      },
      { key: 'username', label: 'Username', input: 'text' },
      { key: 'url', label: 'Login page', input: 'text' },
      { key: 'owner', label: 'Owned by', input: 'text' },
      { key: 'expiry_date', label: 'Expires', kind: 'date', input: 'date' },
      { key: 'last_verified_at', label: 'Last verified', kind: 'date' },
      { key: 'notes', label: 'Notes', input: 'textarea' },
    ],
    link: (r) => s(r.url),
    flag: (r) => (/personal/i.test(s(r.owner)) ? 'Personal account' : ''),
  },
  accounts: {
    key: 'accounts',
    secret: true,
    label: 'Logins',
    lede: 'Website admin, social, email and tool logins.',
    titleField: 'platform',
    meta: (r) => join(s(r.entity), s(r.username)),
    fields: [
      { key: 'platform', label: 'Platform', input: 'text', required: true },
      { key: 'entity', label: 'Company', input: 'select', options: ENTITY_NAMES },
      { key: 'username', label: 'Username / login', input: 'text' },
      { key: 'url', label: 'Login page', input: 'text' },
      { key: 'notes', label: 'Notes', input: 'textarea' },
    ],
    link: (r) => s(r.url),
    flag: (r) => (PERSONAL_EMAIL.test(s(r.username)) ? 'Personal email login' : ''),
  },
  subscriptions: {
    key: 'subscriptions',
    secret: true,
    label: 'Subscriptions',
    lede: 'Paid tools and when they bill.',
    dateField: 'renews_on',
    titleField: 'item',
    meta: (r) =>
      join(s(r.billing_cycle), s(r.username), [s(r.cost), s(r.currency)].filter(Boolean).join(' ')),
    fields: [
      { key: 'item', label: 'Service', input: 'text', required: true },
      {
        key: 'billing_cycle',
        label: 'Billing cycle',
        input: 'select',
        options: ['monthly', 'quarterly', 'yearly', 'one-time'],
      },
      { key: 'username', label: 'Username', input: 'text' },
      { key: 'renews_on', label: 'Renews', kind: 'date', input: 'date' },
      { key: 'cost', label: 'Cost', input: 'number' },
      {
        key: 'currency',
        label: 'Currency',
        input: 'select',
        options: ['SAR', 'AED', 'QAR', 'INR', 'USD', 'EUR'],
      },
      { key: 'last_verified_at', label: 'Last verified', kind: 'date' },
      { key: 'notes', label: 'Notes', input: 'textarea' },
    ],
  },
  phone_numbers: {
    key: 'phone_numbers',
    label: 'Phone numbers',
    lede: 'Who uses each number, and which company it belongs to.',
    titleField: 'number',
    meta: (r) =>
      join(s(r.holder) ? `Used by ${s(r.holder)}` : '', s(r.entity), s(r.market), s(r.used_for)),
    fields: [
      { key: 'number', label: 'Number', input: 'text', required: true },
      { key: 'holder', label: 'Used by', input: 'text' },
      { key: 'entity', label: 'Belongs to', input: 'select', options: ENTITY_NAMES },
      {
        key: 'market',
        label: 'Market',
        input: 'select',
        options: ['SA', 'AE', 'QA', 'IN', 'Other'],
      },
      { key: 'used_for', label: 'Used for', input: 'text' },
      { key: 'notes', label: 'Notes', input: 'textarea' },
    ],
    link: (r) => (r.number ? `https://wa.me/${s(r.number).replace(/[^0-9]/g, '')}` : ''),
    flag: (r) => (s(r.holder) ? '' : 'No one assigned'),
  },
}

export const SECTION_KEYS = Object.keys(SECTIONS) as SectionKey[]

// --- dates --------------------------------------------------------------
export function today(): Date {
  return new Date(new Date().toISOString().slice(0, 10))
}

export function daysUntil(dateStr: string | number | null): number | null {
  if (!dateStr) return null
  const t = new Date(String(dateStr).slice(0, 10))
  if (isNaN(t.getTime())) return null
  return Math.round((t.getTime() - today().getTime()) / 86400000)
}

/** Plain-language gap. Brief §8: "31 days", "8 days over" — never "31d" or "-8". */
export function phrase(n: number | null): string {
  if (n === null) return 'No date'
  if (n < 0) {
    const d = -n
    return d === 1 ? '1 day over' : `${d} days over`
  }
  if (n === 0) return 'Today'
  if (n === 1) return 'Tomorrow'
  if (n <= 90) return `${n} days`
  if (n < 400) return `${Math.round(n / 30)} months`
  const yrs = n / 365
  return `${yrs < 3 ? yrs.toFixed(1) : Math.round(yrs)} years`
}

export type Tone = 'red' | 'amber' | 'green' | 'grey'

/** Brief §7: red = past due, amber = within 45 days, green = healthy. */
export function tone(n: number | null): Tone {
  if (n === null) return 'grey'
  if (n < 0) return 'red'
  if (n <= 45) return 'amber'
  return 'green'
}

export const GROUPS = ['Past due', 'Coming up', 'Fine for now', 'No date recorded'] as const
export type Group = (typeof GROUPS)[number]

export function groupOf(n: number | null): Group {
  if (n === null) return 'No date recorded'
  if (n < 0) return 'Past due'
  if (n <= 45) return 'Coming up'
  return 'Fine for now'
}

export interface DatedItem {
  section: SectionKey
  id: string
  name: string
  when: string
  n: number
}

/** Every record that carries a date, with its day-gap, soonest first. */
export function datedItems(data: RegisterData): DatedItem[] {
  const out: DatedItem[] = []
  for (const key of SECTION_KEYS) {
    const sec = SECTIONS[key]
    if (!sec.dateField) continue
    for (const r of data[key]) {
      const when = s(r[sec.dateField])
      const n = daysUntil(when)
      if (n === null) continue
      out.push({ section: key, id: s(r.id), name: s(r[sec.titleField]), when, n })
    }
  }
  return out.sort((a, b) => a.n - b.n)
}

export function overdueCount(data: RegisterData, key: SectionKey): number {
  const sec = SECTIONS[key]
  if (!sec.dateField) return 0
  return data[key].filter((r) => {
    const n = daysUntil(r[sec.dateField!])
    return n !== null && n < 0
  }).length
}

export function searchText(r: Rec): string {
  return Object.entries(r)
    .filter(([k]) => k !== 'id' && k !== 'created_at' && k !== 'updated_at' && k !== 'has_password')
    .map(([, v]) => s(v))
    .join(' ')
    .toLowerCase()
}
